import { __rest } from "tslib";
import * as React from "react";
import { cn } from "../../lib/utils";
/**
 * @example
 *
 * ```jsx
 * import {
 *   Button,
 *   Card,
 *   CardContent,
 *   CardDescription,
 *   CardFooter,
 *   CardHeader,
 *   CardTitle,
 *   Input,
 *   Label,
 *   Select,
 *   SelectContent,
 *   SelectItem,
 *   SelectTrigger,
 *   SelectValue,
 * } from "@skve/components"
 *
 * function Demo() {
 *   return (
 *     <Card className="w-[350px]">
 *       <CardHeader>
 *         <CardTitle>Create project</CardTitle>
 *         <CardDescription>Deploy your new project in one-click.</CardDescription>
 *       </CardHeader>
 *       <CardContent>
 *         <form>
 *           <div className="grid w-full items-center gap-4">
 *             <div className="flex flex-col space-y-1.5">
 *               <Label htmlFor="name">Name</Label>
 *               <Input id="name" placeholder="Name of your project" />
 *             </div>
 *             <div className="flex flex-col space-y-1.5">
 *               <Label htmlFor="framework">Framework</Label>
 *               <Select>
 *                 <SelectTrigger id="framework">
 *                   <SelectValue placeholder="Select" />
 *                 </SelectTrigger>
 *                 <SelectContent position="popper">
 *                   <SelectItem value="next">Next.js</SelectItem>
 *                   <SelectItem value="sveltekit">SvelteKit</SelectItem>
 *                   <SelectItem value="astro">Astro</SelectItem>
 *                   <SelectItem value="nuxt">Nuxt.js</SelectItem>
 *                 </SelectContent>
 *               </Select>
 *             </div>
 *           </div>
 *         </form>
 *       </CardContent>
 *       <CardFooter className="flex justify-between">
 *         <Button variant="outline">Cancel</Button>
 *         <Button>Deploy</Button>
 *       </CardFooter>
 *     </Card>
 *   )
 * }
 * ```
 */
const Card = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("div", Object.assign({ ref: ref, className: cn("rounded-lg bg-card text-card-foreground shadow-lg dark:shadow-none", className) }, props)));
});
Card.displayName = "Card";
const CardHeader = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("div", Object.assign({ ref: ref, className: cn("flex flex-col space-y-1.5 p-4 pt-6 bg-card-header rounded-t-lg", className) }, props)));
});
CardHeader.displayName = "CardHeader";
const CardTitle = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("h3", Object.assign({ ref: ref, className: cn("text-2xl font-semibold leading-none tracking-tight", className) }, props)));
});
CardTitle.displayName = "CardTitle";
const CardDescription = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("p", Object.assign({ ref: ref, className: cn("text-sm text-muted-foreground", className) }, props)));
});
CardDescription.displayName = "CardDescription";
const CardContent = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("div", Object.assign({ ref: ref, className: cn("p-4 pb-6", className) }, props)));
});
CardContent.displayName = "CardContent";
const CardFooter = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("div", Object.assign({ ref: ref, className: cn("flex items-center p-4 pb-6 pt-0", className) }, props)));
});
CardFooter.displayName = "CardFooter";
export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, };
//# sourceMappingURL=card.js.map